import { defineStore } from "pinia"
import { computed, ref, type Ref } from "vue"
import { Project } from "@/models/project.model"
import { isPopulated, lazyFieldId } from "@/models/types/lazy-field"

export const useProjectStore = defineStore("projectsStore", () => {
  const projects: Ref<Project[]> = ref([])
  // One index per store, regardless of how many dropdowns mount or unmount.
  const projectIndex = computed(() => buildProjectIndex(projects.value))

  return {
    projects,
    projectIndex
  }
})

interface ProjectEntry {
  project: Project
  groupId?: string
  groupName: string
  name: string
  label: string
  searchText: string
}

interface ProjectIndex {
  byId: Map<string | undefined, ProjectEntry>
  groups: Map<string, ProjectEntry[]>
  ordered: ProjectEntry[]
}

const collator = new Intl.Collator("nl", { numeric: true })

function buildProjectIndex(items: Project[]): ProjectIndex {
  const byId = new Map<string | undefined, ProjectEntry>()
  const groups = new Map<string, ProjectEntry[]>()
  const ordered: ProjectEntry[] = []
  for (const project of items) {
    const label = projectLabel(project)
    const groupName = isPopulated(project.projectGroup) ? project.projectGroup.name : ""
    const entry: ProjectEntry = {
      project,
      groupId: lazyFieldId(project.projectGroup),
      groupName,
      name: project.name,
      label,
      searchText: `${groupName} ${label}`.toLowerCase()
    }
    byId.set(project.id, entry)
    ordered.push(entry)
    if (project.isActive && entry.groupId) {
      const members = groups.get(entry.groupId) ?? []
      members.push(entry)
      groups.set(entry.groupId, members)
    }
  }
  ordered.sort((a, b) => {
    // Sort groups by group name alongside individual projects by project name.
    const order = collator.compare(a.groupName || a.name, b.groupName || b.name)
    if (order) return order
    if (a.groupId !== b.groupId) {
      const groupOrder = collator.compare(a.groupId ?? "", b.groupId ?? "")
      if (groupOrder) return groupOrder
    }
    return collator.compare(a.name, b.name)
  })
  return { byId, groups, ordered }
}

export function projectLabel(project: Project): string {
  const label = project.getDisplayText()
  const groupName = isPopulated(project.projectGroup) ? project.projectGroup.name : ""
  return groupName && label.startsWith(`${groupName} ${project.name}`)
    ? label.slice(groupName.length + 1)
    : label
}
